import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Bank, Copy, Check, Warning, PencilSimple, DeviceMobile } from 'phosphor-react';

const BANK_ACCOUNT_HOLDER = process.env.REACT_APP_BANK_ACCOUNT_HOLDER || '';
const BANK_IBAN = process.env.REACT_APP_BANK_IBAN || '';
const BANK_BIC = process.env.REACT_APP_BANK_BIC || '';
const BANK_NAME = process.env.REACT_APP_BANK_NAME || '';

// Formats an IBAN into groups of 4 characters for display, e.g. BE68 5390 0754 7034
const formatIban = (iban) => iban.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();

// Builds an EPC069-12 ("SEPA QR") payload so European banking apps can
// scan the code and pre-fill the transfer form.
const buildEpcQrPayload = ({ iban, bic, name, amount, reference }) => {
  const lines = [
    'BCD',
    '002',
    '1',
    'SCT',
    bic || '',
    (name || '').slice(0, 70),
    iban.replace(/\s+/g, ''),
    `EUR${amount.toFixed(2)}`,
    '',
    '',
    reference.slice(0, 140),
    ''
  ];
  return lines.join('\n');
};

const CopyableField = ({ label, value, displayValue }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="font-mono text-sm md:text-base text-gray-900 break-all">{displayValue || value}</div>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className="flex-shrink-0 flex items-center gap-1 px-3 py-2 text-xs md:text-sm font-medium text-ambre-700 bg-ambre-50 hover:bg-ambre-100 rounded-lg transition-colors"
      >
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
};

const BankTransferPayment = ({ orderNumber, totalAmount, loading, onConfirm, onCancel }) => {
  const { t } = useTranslation();

  const isConfigured = Boolean(BANK_IBAN && BANK_ACCOUNT_HOLDER);

  if (!isConfigured) {
    return (
      <div className="w-full bg-gray-50 p-6 md:p-8">
        <div className="text-center">
          <Warning size={40} className="text-amber-500 mx-auto mb-3" />
          <p className="text-gray-700 mb-4">{t('bankTransfer.notConfigured')}</p>
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            {t('checkout.back')}
          </button>
        </div>
      </div>
    );
  }

  const qrPayload = buildEpcQrPayload({
    iban: BANK_IBAN,
    bic: BANK_BIC,
    name: BANK_ACCOUNT_HOLDER,
    amount: totalAmount,
    reference: orderNumber
  });

  return (
    <div className="w-full bg-gray-50 p-4 md:p-6 space-y-6">
      <div className="flex items-start gap-3">
        <Bank size={20} className="text-ambre-600 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="font-medium text-gray-900 text-sm md:text-base">{t('bankTransfer.title')}</h4>
          <p className="text-gray-600 text-xs md:text-sm mt-1">{t('bankTransfer.instructions')}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Bank details - manual transfer */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
          <div className="flex items-center gap-2 pb-3 mb-1 border-b border-gray-100">
            <PencilSimple size={16} className="text-gray-500 flex-shrink-0" />
            <h5 className="text-sm font-semibold text-gray-700">{t('bankTransfer.manualTitle')}</h5>
          </div>
          <div className="divide-y divide-gray-100">
            <CopyableField label={t('bankTransfer.accountHolder')} value={BANK_ACCOUNT_HOLDER} />
            <CopyableField label={t('bankTransfer.iban')} value={BANK_IBAN.replace(/\s+/g, '')} displayValue={formatIban(BANK_IBAN)} />
            {BANK_BIC && <CopyableField label={t('bankTransfer.bic')} value={BANK_BIC} />}
            {BANK_NAME && (
              <div className="py-2">
                <div className="text-xs text-gray-500">{t('bankTransfer.bankName')}</div>
                <div className="text-sm md:text-base text-gray-900">{BANK_NAME}</div>
              </div>
            )}
            <CopyableField label={t('bankTransfer.amount')} value={totalAmount.toFixed(2)} displayValue={`€${totalAmount.toFixed(2)}`} />
            <CopyableField label={t('bankTransfer.reference')} value={orderNumber} />
          </div>
        </div>

        {/* QR code - banking app */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center h-full flex flex-col">
          <div className="flex items-center justify-center gap-2 pb-3 mb-1 border-b border-gray-100">
            <DeviceMobile size={16} className="text-gray-500 flex-shrink-0" />
            <h5 className="text-sm font-semibold text-gray-700">{t('bankTransfer.appTitle')}</h5>
          </div>
          <p className="text-xs text-gray-500 mb-3">{t('bankTransfer.scanToPayDesc')}</p>
          <div className="flex-1 flex items-center justify-center">
            <div className="inline-block p-3 border-2 border-gray-200 rounded-lg">
              <QRCodeSVG value={qrPayload} size={180} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="w-full md:flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {t('checkout.back')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className="w-full md:flex-1 px-4 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              {t('checkout.processing')}
            </>
          ) : (
            t('bankTransfer.confirmButton')
          )}
        </button>
      </div>
    </div>
  );
};

export default BankTransferPayment;
